/**
 * useDeadlineAlerts — checks for approaching/overdue deadlines
 * and shows toast notifications on dashboard load.
 *
 * Only runs once per session to avoid spamming the user.
 */

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import type { TemplateMeta } from "../utils/templateApi";

const SESSION_FLAG = "zap_deadline_alert_shown";

/** How many days before deadline to warn */
const WARN_DAYS = 3;

export function useDeadlineAlerts(templates: TemplateMeta[], isReady: boolean) {
  const hasRun = useRef(false);

  useEffect(() => {
    if (!isReady || templates.length === 0 || hasRun.current) return;

    // Only show once per session
    try {
      if (sessionStorage.getItem(SESSION_FLAG)) return;
    } catch { /* ignore */ }

    hasRun.current = true;

    const now = Date.now();
    const warnThreshold = now + WARN_DAYS * 24 * 60 * 60 * 1000;

    const overdue: TemplateMeta[] = [];
    const approaching: TemplateMeta[] = [];

    for (const t of templates) {
      if (!t.deadline || t.archived || t.status === "live") continue;
      if (t.deadline < now) {
        overdue.push(t);
      } else if (t.deadline < warnThreshold) {
        approaching.push(t);
      }
    }

    if (overdue.length > 0) {
      const names = overdue.slice(0, 3).map((t) => t.clientName).join(", ");
      const extra = overdue.length > 3 ? ` (+${overdue.length - 3})` : "";
      toast.error(
        `\u05D3\u05D3\u05DC\u05D9\u05D9\u05DF \u05E2\u05D1\u05E8! ${overdue.length} \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA: ${names}${extra}`,
        { duration: 8000 },
      );
    }

    if (approaching.length > 0) {
      const names = approaching.slice(0, 3).map((t) => {
        const daysLeft = Math.ceil((t.deadline! - now) / (24 * 60 * 60 * 1000));
        return `${t.clientName} (${daysLeft} \u05D9\u05DE\u05D9\u05DD)`;
      }).join(", ");
      toast.warning(
        `\u05D3\u05D3\u05DC\u05D9\u05D9\u05DF \u05DE\u05EA\u05E7\u05E8\u05D1\u05D9\u05DD: ${names}`,
        { duration: 6000 },
      );
    }

    try {
      sessionStorage.setItem(SESSION_FLAG, "1");
    } catch { /* ignore */ }
  }, [templates, isReady]);
}
